import type { TranslationKey } from '../../types/i18n/translation-keys.type';
import type {
  AuthBootstrapErrorKind,
  AuthBootstrapPhase,
} from '../../types/auth/auth-bootstrap.type';

/**
 * Configuration used to build an auth bootstrap error.
 */
export interface AuthBootstrapErrorOptions {
  /** Whether the current failure can be retried locally. */
  canRetry?: boolean;
  /** Underlying error captured while bootstrapping. */
  cause?: unknown;
  /** High-level error classification. */
  kind: AuthBootstrapErrorKind;
  /** Translation key used to render the user-facing error message. */
  messageKey: TranslationKey;
  /** Bootstrap phase that raised the error. */
  phase?: AuthBootstrapPhase;
}

/**
 * Typed error used to classify failures during frontend auth bootstrap.
 */
export class AuthBootstrapError extends Error {
  /** Whether the current failure can be retried locally. */
  canRetry: boolean;
  /** Underlying error captured while bootstrapping. */
  cause?: unknown;
  /** High-level error classification. */
  kind: AuthBootstrapErrorKind;
  /** Translation key used to render the user-facing error message. */
  messageKey: TranslationKey;
  /** Bootstrap phase that raised the error. */
  phase: AuthBootstrapPhase;

  constructor(message: string, options: AuthBootstrapErrorOptions) {
    super(message);
    this.name = 'AuthBootstrapError';
    this.canRetry = options.canRetry ?? false;
    this.cause = options.cause;
    this.kind = options.kind;
    this.messageKey = options.messageKey;
    this.phase = options.phase ?? 'unknown';
  }
}
